import React, { useState, useEffect } from 'react';
import { Select, Button, Modal, Form, Input, Radio, InputNumber, message, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { topicAPI } from '../services/api';
import { EssayTopic, EssayTypes } from '../types';
import { extractTopicData } from '../utils/topicHelper';

const { Option } = Select;
const { TextArea } = Input;

interface TopicSelectorProps {
  value?: number;
  onChange?: (value: number, topic?: EssayTopic) => void;
  disabled?: boolean;
  allowCreate?: boolean;
}

const TopicSelector: React.FC<TopicSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  allowCreate = true,
}) => {
  const [topics, setTopics] = useState<EssayTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTopic, setEditingTopic] = useState<EssayTopic | null>(null);
  const [form] = Form.useForm();

  // 加载题目列表
  const loadTopics = async () => {
    setLoading(true);
    try {
      const response = await topicAPI.list({ is_active: true, page_size: 100 });
      // 处理可能的嵌套结构
      const topicList = response.data.data || [];
      const processedTopics = topicList.map((item: any) => extractTopicData(item));
      setTopics(processedTopics);
    } catch (error) {
      message.error('加载题目失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTopics();
  }, []);

  // 创建或更新题目
  const handleSubmit = async (values: any) => {
    try {
      if (editingTopic) {
        await topicAPI.update(editingTopic.id, values);
        message.success('题目更新成功');
      } else {
        const response = await topicAPI.create(values);
        message.success('题目创建成功');
        // 选择新创建的题目
        if (onChange && response.data.data) {
          const topicData = extractTopicData(response.data.data);
          onChange(topicData.id, topicData);
        }
      }
      setModalVisible(false);
      form.resetFields();
      setEditingTopic(null);
      loadTopics();
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  // 打开创建/编辑模态框
  const openModal = (topic?: EssayTopic) => {
    if (topic) {
      setEditingTopic(topic);
      form.setFieldsValue(topic);
    } else {
      setEditingTopic(null);
      form.resetFields();
      form.setFieldsValue({
        essay_type: EssayTypes.CONTINUATION,
        word_limit: 150,
      });
    }
    setModalVisible(true);
  };

  // 选择题目时的处理
  const handleSelect = (topicId: number) => {
    const selectedTopic = topics.find(t => t.id === topicId);
    if (onChange) {
      onChange(topicId, selectedTopic);
    }
  };

  // 获取选中的题目
  const selectedTopic = topics.find(t => t.id === value);

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Select
          style={{ flex: 1 }}
          placeholder="请选择作文题目"
          value={value}
          onChange={handleSelect}
          loading={loading}
          disabled={disabled}
          showSearch
          filterOption={(input, option) => {
            const topic = topics.find(t => t.id === option?.value);
            return topic ? topic.title.toLowerCase().includes(input.toLowerCase()) : false;
          }}
        >
          {topics.map(topic => (
            <Option key={topic.id} value={topic.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{topic.title}</span>
                <Space size="small">
                  <Tag color={topic.essay_type === EssayTypes.APPLICATION ? 'blue' : 'green'}>
                    {topic.essay_type === EssayTypes.APPLICATION ? '应用文' : '读后续写'}
                  </Tag>
                  <Tag>{topic.word_limit}词</Tag>
                  {topic.usage_count > 0 && (
                    <Tag color="orange">已用{topic.usage_count}次</Tag>
                  )}
                </Space>
              </div>
            </Option>
          ))}
        </Select>
        {allowCreate && (
          <>
            <Button
              icon={<PlusOutlined />}
              onClick={() => openModal()}
              disabled={disabled}
            >
              新建
            </Button>
            {selectedTopic && (
              <Button
                icon={<EditOutlined />}
                onClick={() => openModal(selectedTopic)}
                disabled={disabled}
              >
                编辑
              </Button>
            )}
          </>
        )}
      </Space.Compact>

      <Modal
        title={editingTopic ? '编辑题目' : '创建新题目'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingTopic(null);
        }}
        onOk={() => form.submit()}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            essay_type: EssayTypes.CONTINUATION,
            word_limit: 150,
          }}
        >
          <Form.Item
            name="title"
            label="题目标题"
            rules={[{ required: true, message: '请输入题目标题' }]}
          >
            <Input placeholder="如：A Run to Remember" />
          </Form.Item>

          <Form.Item
            name="essay_type"
            label="作文类型"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value={EssayTypes.CONTINUATION}>读后续写</Radio>
              <Radio value={EssayTypes.APPLICATION}>应用文写作</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="word_limit"
            label="字数要求"
            rules={[{ required: true, message: '请输入字数要求' }]}
          >
            <InputNumber
              min={50}
              max={300}
              step={10}
              addonAfter="词"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="topic"
            label="话题/主题（可选）"
          >
            <Input placeholder="如：友谊、冒险、成长等" />
          </Form.Item>

          <Form.Item
            name="requirements"
            label="详细要求"
            extra="可以包含续写段落开头、应用文格式要求等"
          >
            <TextArea
              rows={8}
              placeholder="请输入作文的详细要求..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TopicSelector;